import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import {
  formatDate,
  formatMonthYear,
} from "../../../Components/Common/dateFormat";

function TransactionsTable({
  rows = [],
  columns = [],
  onRowSelect,
  onSelectAll,
  selectedRows,
  selectAll,
  maxHeight,
  showViewColumn = false,
}) {
  const { mode } = useThemeContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const borderColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const textColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const backgroundColor1 =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const deleteColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const colorSuccess =
    mode === "dark" ? DARKCOLORS.Secondary700 : LIGHTCOLORS.Secondary700;
  const btnColor =
    mode === "light" ? LIGHTCOLORS.GreyScale80 : DARKCOLORS.GreyScale80;
  const btnTextColor =
    mode === "light"
      ? LIGHTCOLORS.HeadingDarkBackground
      : DARKCOLORS.HeadingDarkBackground;

  const statusBadgeStyles = (status) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, "_");

    switch (normalizedStatus) {
      case "completed":
        return {
          backgroundColor:
            mode === "dark" ? DARKCOLORS.Success100 : LIGHTCOLORS.Success100,
          border: `1px solid ${
            mode === "dark" ? DARKCOLORS.Success500 : LIGHTCOLORS.Success500
          }`,
          color:
            mode === "dark" ? DARKCOLORS.Success700 : LIGHTCOLORS.Success700,
        };

      case "failed":
        return {
          backgroundColor:
            mode === "dark" ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100,
          border: `1px solid ${
            mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500
          }`,
          color: mode === "dark" ? DARKCOLORS.Danger600 : LIGHTCOLORS.Danger600,
        };

      case "partial_failure":
        return {
          backgroundColor: "#F2D773",
          border: "1px solid #F2D773",
          color: "#000000",
        };

      default:
        return {
          backgroundColor: "transparent",
          border: "1px solid transparent",
          color: textColor,
        };
    }
  };

  // Function to format status icons based on the status
  const renderStatusIcon = (status) => {
    if (!status) return null;

    // Normalize whitespace so "Partial Failure" (API) and "partial_failure" (filter value) match
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");

    if (normalizedStatus === "completed") {
      return (
        <CheckCircleRoundedIcon
          style={{
            color: colorSuccess,
            width: 20,
            height: 20,
            mr: 1,
          }}
        />
      );
    } else if (
      normalizedStatus === "failed" ||
      normalizedStatus === "partial_failure"
    )
      return <CancelRoundedIcon style={{ color: deleteColor }} />;
    else return;
  };

  const translateStatus = (status) => {
    // Same normalization as renderStatusIcon: "Partial Failure" -> "partial_failure"
    const statusKey =
      status?.toLowerCase().replace(/\s+/g, "_") === "partial_failure"
        ? "partialFailure"
        : status?.toLowerCase();
    return t(`transactions.${statusKey}`, { defaultValue: status });
  };

  const translatePaymentType = (row) => {
    if (row.transactionType === "RETURN") return t("transactions.bowlReturn");
    if (row.paymentType === "Membership Card")
      return t("transactions.nafWallet");
    if (row.paymentType === "Credit/Debit Card")
      return t("membershipCards.cardType");
    if (row.paymentType === "Credit Card") return t("dashboard.creditCard");
    return row.paymentType || "-";
  };
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;
  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  return (
    <TableContainer
      sx={{
        maxHeight: maxHeight || "auto",
        overflowX: "auto", // scrolling must live here, not on <Table>
        WebkitOverflowScrolling: "touch",
        // Scrollbar styles must live on the scrolling element (this container)
        "&::-webkit-scrollbar": {
          width: "10px",
          height: "12px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: scroll1,
          borderRadius: "10px",
          border: `1px solid ${scroll}`,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: scroll,
          borderRadius: "10px",
          border: "3px solid transparent",
          backgroundClip: "padding-box",
          "&:hover": {
            backgroundColor: scroll,
          },
        },
      }}
    >
      <Table
        // stickyHeader
        sx={{
          borderCollapse: "collapse",
          marginX: "4px",
          width: "max-content", // let it grow past the viewport
          minWidth: "99%", // but never shrink below the container
          tableLayout: "auto",
          overflowX: "auto",
          "& .MuiTableCell-root": {
            border: "none",
          },
          "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
            {
              borderBottom: `0.5px solid ${borderColor2}`,
            },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              borderTop: `0.5px solid ${borderColor2}`,
              borderBottom: `0.5px solid ${borderColor2}`,
            }}
          >
            <TableCell sx={{ width: "50px", padding: "6px 12px" }}>
              <Checkbox
                checked={selectAll}
                indeterminate={
                  selectedRows?.length > 0 &&
                  selectedRows?.length < rows?.length
                }
                onChange={onSelectAll}
              />
            </TableCell>
            {columns.map((column, index) => (
              <TableCell
                key={index}
                align={column.align || "left"}
                sx={{
                  color: textColor,
                  padding: "6px 10px",
                  minWidth: column.minWidth || "auto",
                  maxWidth: column.maxWidth || "none",
                  textAlign: column.align || "left",
                  width: column.width || "auto",
                }}
                className="venidng-sec-card-text main-table-cell"
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.length > 0 ? (
            rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  borderLeft: row.isHighlighted
                    ? "5px solid rgba(108, 156, 210, 0.57)"
                    : "none", // Orange left border
                  boxShadow: row.isHighlighted
                    ? "0px 0px 8px rgba(133, 173, 173, 0.75)"
                    : "none",
                  "&:nth-of-type(even)": {
                    background: backgroundColor1,
                  },
                }}
              >
                <TableCell
                  sx={{
                    width: "50px",
                    padding: "6px 12px",
                    borderRadius: "10px 0px 0px 10px",
                  }}
                >
                  <Checkbox
                    checked={selectedRows.some(
                      (selectedRow) => selectedRow.id === row.id,
                    )}
                    onChange={() => onRowSelect(row)}
                  />
                </TableCell>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={column.align || "left"}
                    sx={{
                      color: textColor,
                      padding: "5px 10px",
                      maxWidth: "100px",
                      textAlign: column.align || "left",
                      width: column.width || "auto",
                      ...(colIndex === columns.length - 1 &&
                        !showViewColumn && {
                          borderRadius: "0px 10px 10px 0px",
                        }),
                    }}
                    className="add-machine  main-table-cell"
                  >
                    {column.renderCell ? (
                      column.renderCell(row) // Only productSummary has renderCell (Tooltip)
                    ) : column.field === "taxableAmount" ||
                      column.field === "tax" ||
                      column.field === "vatAmount" ||
                      column.field === "netAmount" ||
                      column.field === "grossAmount" ||
                      column.field === "finalPrice" ||
                      column.field === "dueAmount" ? (
                      `€  ${row[column.field] != null && !isNaN(row[column.field]) ? parseFloat(row[column.field]).toFixed(2) : row[column.field]}`
                    ) : column.field === "month" ? (
                      formatMonthYear(row[column.field])
                    ) : column.field === "filedDate" ? (
                      formatDate(row[column.field])
                    ) : column.field === "temp" ||
                      column.field === "cabinetTemp" ? (
                      `${row[column.field]} °C`
                    ) : column.field === "status" ? (
                      <Tooltip
                        title={
                          row[column.field]
                            ?.toLowerCase()
                            .replace(/\s+/g, "_") === "failed" ||
                          row[column.field]
                            ?.toLowerCase()
                            .replace(/\s+/g, "_") === "partial_failure"
                            ? row.reason || "-"
                            : ""
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "6px 10px",
                            borderRadius: "99px",
                            backgroundColor: statusBadgeStyles(
                              row[column.field],
                            ).backgroundColor,
                            border: statusBadgeStyles(row[column.field]).border,
                            cursor:
                              row[column.field]
                                ?.toLowerCase()
                                .replace(/\s+/g, "_") === "failed" ||
                              row[column.field]
                                ?.toLowerCase()
                                .replace(/\s+/g, "_") === "partial_failure"
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: statusBadgeStyles(row[column.field]).color,
                              fontFamily: "Satoshi-medium",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {translateStatus(row[column.field])}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : column.field == "paymentType" ? (
                      <Tooltip title={translatePaymentType(row)} arrow>
                        <span>{translatePaymentType(row)}</span>
                      </Tooltip>
                    ) : column.field === "offer" ? (
                      <Tooltip title={row[column.field] || "-"} arrow>
                        <span>
                          {row[column.field] != null ? row[column.field] : "-"}
                        </span>
                      </Tooltip>
                    ) : column.render ? (
                      column.render(row[column.field], row)
                    ) : row[column.field] != null ? (
                      row[column.field]
                    ) : (
                      "-"
                    )}
                  </TableCell>
                ))}
                {/* Add the "View" button if the month field exists */}
                {showViewColumn && (
                  <TableCell
                    align="center"
                    sx={{ borderRadius: "0px 10px 10px 0px" }}
                  >
                    {row.month && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: btnColor,
                          color: btnTextColor,
                          borderRadius: "50px",
                          padding: "6px  12px  3px 12px",
                          textTransform: "none",
                        }}
                        onClick={() =>
                          navigate(ROUTES.TAXREPORT, {
                            state: { month: row.month },
                          })
                        }
                      >
                        {t("dashboard.view")}
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow sx={{ height: "50vh" }}>
              <TableCell
                colSpan={columns.length + (showViewColumn ? 2 : 1)}
                align="center"
                sx={{
                  verticalAlign: "middle",
                  textAlign: "center",
                }}
              >
                {t("membershipCards.Nodataavailable")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TransactionsTable;
